import { useHotkeys } from 'react-hotkeys-hook';
import { useMatches } from 'react-router-dom';

import { Pane } from '@/components/Pane';
import XIcon from '@/icons/XIcon.svg?react';
import { cn, utils } from '@/utils/tailwind';

interface KeyboardShortcutProps {
  keys: (string | JSX.Element)[];
  className?: string;
  soloClassName?: string;
}

export function KeyboardShortcut({
  keys,
  className,
  soloClassName = '',
}: KeyboardShortcutProps) {
  return keys.length > 1 ? (
    <div
      className={cn(
        'ml-2 flex flex-row gap-1 rounded-[3px] px-1.5 py-1 text-center font-mono text-xs font-normal',
        'border border-secondary bg-secondary text-primary',
        className
      )}
    >
      {keys.map((key, idx) => (
        <div key={idx} className={soloClassName}>
          {key}
        </div>
      ))}
    </div>
  ) : (
    <div
      className={cn(
        'my-auto ml-2 h-fit w-[22px] rounded-[3px] py-0.5 text-center font-mono text-xs font-normal',
        'border border-secondary bg-secondary text-primary',
        className,
        soloClassName
      )}
    >
      {keys[0]}
    </div>
  );
}

function KeyboardShortcutInstructions({ hotkey }) {
  return (
    <div className="flex items-center justify-between border-b border-secondary py-3">
      <div className="text-md">{hotkey.description}</div>
      <div className="flex flex-row">
        <KeyboardShortcut
          keys={hotkey.keys}
          className={cn(hotkey.keys.length == 1 ? '' : 'py-0.5', 'text-md')}
          soloClassName={hotkey.keys.length == 1 ? 'w-7' : ''}
        />
      </div>
    </div>
  );
}

export function KeyboardShortcutsSidePane({ isOpen, doClose }) {
  const matches = useMatches();
  const match = matches.find((m) => (m.handle as any)?.hotkeys);
  const hotkeys = (match?.handle as any)?.hotkeys;

  useHotkeys('esc', doClose, { enabled: isOpen });

  return (
    <Pane
      open={isOpen}
      onClose={doClose}
      title={'Keyboard Shortcuts'}
      className="px-4 pt-4"
      noBackArrow={true}
      titleClassName="border-none px-4 pb-0"
      dialogStyle={{ marginLeft: 'auto', width: '30rem' }}
      topBarRightElement={
        <button type="button" className={utils.button} onClick={doClose}>
          <XIcon />
        </button>
      }
    >
      {hotkeys?.length > 0 ? (
        hotkeys.map((hotkey, idx) => (
          <KeyboardShortcutInstructions key={idx} hotkey={hotkey} />
        ))
      ) : (
        <div className="text-md py-3">No keyboard shortcuts for this page.</div>
      )}
    </Pane>
  );
}
